import React, { useState } from 'react'

import FeatureNotEnabled from '../../components/FeatureNotEnabled'
import { Card } from '../../components/UI'
import {
    Zap, ZapOff, ShieldCheck, AlertTriangle, Clock, Activity,
    ArrowRight, Settings2, Info, CheckCircle2, XCircle, Timer
} from 'lucide-react'

// ============================================
// Simulación del estado del Circuit Breaker
// ============================================

type CBState = 'closed' | 'open' | 'half-open'

interface CBConfig {
    failureThreshold: number
    resetTimeout: number
    halfOpenMax: number
    monitorWindow: number
}

interface TransitionLog {
    time: string
    from: CBState
    to: CBState
    reason: string
}

const DEFAULT_CONFIG: CBConfig = {
    failureThreshold: 5,
    resetTimeout: 30,
    halfOpenMax: 3,
    monitorWindow: 60,
}

const STATE_STYLES: Record<CBState, { bg: string; border: string; text: string; icon: React.ReactNode; label: string; desc: string }> = {
    closed: {
        bg: 'bg-neon-green/5',
        border: 'border-neon-green/20',
        text: 'text-neon-green',
        icon: <CheckCircle2 size={32} />,
        label: 'CLOSED',
        desc: 'Circuito cerrado — tráfico normal. Todas las requests fluyen a la API.'
    },
    open: {
        bg: 'bg-red-500/5',
        border: 'border-red-500/20',
        text: 'text-red-400',
        icon: <XCircle size={32} />,
        label: 'OPEN',
        desc: 'Circuito abierto — requests bloqueadas. Se usa caché/fallback local.'
    },
    'half-open': {
        bg: 'bg-yellow-500/5',
        border: 'border-yellow-500/20',
        text: 'text-yellow-400',
        icon: <AlertTriangle size={32} />,
        label: 'HALF-OPEN',
        desc: 'Probando recuperación — requests limitadas para verificar si la API responde.'
    },
}

export default function CircuitBreakerPage() {
    const [state, setState] = useState<CBState>('closed')
    const [config, setConfig] = useState<CBConfig>(DEFAULT_CONFIG)
    const [failures, setFailures] = useState(0)
    const [probesSent, setProbesSent] = useState(0)
    const [logs, setLogs] = useState<TransitionLog[]>([
        { time: '02:30:00', from: 'closed', to: 'open', reason: '5 fallos consecutivos en validación de licencia' },
        { time: '02:30:30', from: 'open', to: 'half-open', reason: 'Timeout de 30s expirado, probando recuperación' },
        { time: '02:30:35', from: 'half-open', to: 'closed', reason: 'Probe exitoso, API respondió 200 OK' },
    ])
    const [tab, setTab] = useState<'monitor' | 'config' | 'docs'>('monitor')

    const addLog = (from: CBState, to: CBState, reason: string) => {
        setLogs(prev => [{
            time: new Date().toLocaleTimeString('es-ES'),
            from, to, reason
        }, ...prev].slice(0, 50))
    }

    const simulateFailure = () => {
        const newFailures = failures + 1
        setFailures(newFailures)
        if (state === 'closed' && newFailures >= config.failureThreshold) {
            addLog('closed', 'open', `${newFailures} fallos alcanzados (threshold: ${config.failureThreshold})`)
            setState('open')
            setFailures(0)
        } else if (state === 'half-open') {
            addLog('half-open', 'open', 'Probe falló durante half-open')
            setState('open')
            setProbesSent(0)
        }
    }

    const simulateSuccess = () => {
        if (state === 'half-open') {
            const newProbes = probesSent + 1
            setProbesSent(newProbes)
            if (newProbes >= config.halfOpenMax) {
                addLog('half-open', 'closed', `${newProbes} probes exitosos, API recuperada`)
                setState('closed')
                setProbesSent(0)
                setFailures(0)
            }
        } else if (state === 'closed') {
            setFailures(0)
        }
    }

    const simulateTimeout = () => {
        if (state === 'open') {
            addLog('open', 'half-open', `Reset timeout expirado (${config.resetTimeout}s)`)
            setState('half-open')
        }
    }

    const resetCB = () => {
        setState('closed')
        setFailures(0)
        setProbesSent(0)
    }

    const current = STATE_STYLES[state]
    const tabs = [
        { id: 'monitor' as const, label: 'Monitor', icon: <Activity size={16} /> },
        { id: 'config' as const, label: 'Configuración', icon: <Settings2 size={16} /> },
        { id: 'docs' as const, label: 'Documentación', icon: <Info size={16} /> },
    ]

    return (
        <div className="p-6 max-w-5xl mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                    <Zap className="text-neon-blue" /> Circuit Breaker
                </h1>
                <p className="text-gray-500 text-sm">Simulación del patrón Circuit Breaker</p>
            </div>

            <FeatureNotEnabled moduleName="Circuit Breaker" />

            {/* Tabs */}
            <div className="flex gap-1 mb-6 bg-charcoal rounded-lg p-1 border border-white/5">
                {tabs.map(t => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20' : 'text-gray-500 hover:text-white'
                            }`}
                    >
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {/* Tab: Monitor */}
            {tab === 'monitor' && (
                <>
                    {/* Estado actual */}
                    <Card className={`mb-6 ${current.bg} border ${current.border}`}>
                        <div className="flex items-center gap-6">
                            <div className={current.text}>{current.icon}</div>
                            <div className="flex-1">
                                <div className="flex items-center gap-3">
                                    <span className={`text-2xl font-bold font-mono ${current.text}`}>{current.label}</span>
                                    <span className={`w-3 h-3 rounded-full animate-pulse ${state === 'closed' ? 'bg-neon-green' : state === 'open' ? 'bg-red-500' : 'bg-yellow-500'}`} />
                                </div>
                                <p className="text-gray-400 text-sm mt-1">{current.desc}</p>
                            </div>
                            <div className="text-right">
                                <div className="text-xs text-gray-500">Fallos consecutivos</div>
                                <div className="text-2xl font-mono font-bold text-white">{failures}<span className="text-gray-600 text-sm">/{config.failureThreshold}</span></div>
                            </div>
                        </div>
                    </Card>

                    {/* Diagrama de estados */}
                    <Card className="mb-6">
                        <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
                            <Activity size={16} className="text-neon-blue" /> Diagrama de Estados
                        </h2>
                        <div className="flex items-center justify-center gap-4 py-6">
                            {/* CLOSED */}
                            <div className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${state === 'closed' ? 'border-neon-green bg-neon-green/5 scale-110' : 'border-white/10 bg-white/[0.02] opacity-50'}`}>
                                <CheckCircle2 size={28} className={state === 'closed' ? 'text-neon-green' : 'text-gray-600'} />
                                <span className={`text-xs font-bold font-mono ${state === 'closed' ? 'text-neon-green' : 'text-gray-600'}`}>CLOSED</span>
                            </div>

                            <div className="flex flex-col items-center gap-1">
                                <ArrowRight size={20} className="text-red-400" />
                                <span className="text-[10px] text-gray-600">N fallos</span>
                            </div>

                            {/* OPEN */}
                            <div className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${state === 'open' ? 'border-red-500 bg-red-500/5 scale-110' : 'border-white/10 bg-white/[0.02] opacity-50'}`}>
                                <XCircle size={28} className={state === 'open' ? 'text-red-400' : 'text-gray-600'} />
                                <span className={`text-xs font-bold font-mono ${state === 'open' ? 'text-red-400' : 'text-gray-600'}`}>OPEN</span>
                            </div>

                            <div className="flex flex-col items-center gap-1">
                                <ArrowRight size={20} className="text-yellow-400" />
                                <span className="text-[10px] text-gray-600">timeout</span>
                            </div>

                            {/* HALF-OPEN */}
                            <div className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${state === 'half-open' ? 'border-yellow-500 bg-yellow-500/5 scale-110' : 'border-white/10 bg-white/[0.02] opacity-50'}`}>
                                <AlertTriangle size={28} className={state === 'half-open' ? 'text-yellow-400' : 'text-gray-600'} />
                                <span className={`text-xs font-bold font-mono ${state === 'half-open' ? 'text-yellow-400' : 'text-gray-600'}`}>HALF-OPEN</span>
                            </div>
                        </div>
                    </Card>

                    {/* Controles de simulación */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                        <button onClick={simulateFailure} className="px-4 py-3 bg-red-500/10 text-red-400 border border-red-500/20 rounded-xl text-sm font-medium hover:bg-red-500/20 transition-all flex items-center justify-center gap-2">
                            <ZapOff size={16} /> Simular Fallo
                        </button>
                        <button onClick={simulateSuccess} className="px-4 py-3 bg-neon-green/10 text-neon-green border border-neon-green/20 rounded-xl text-sm font-medium hover:bg-neon-green/20 transition-all flex items-center justify-center gap-2">
                            <Zap size={16} /> Simular Éxito
                        </button>
                        <button onClick={simulateTimeout} className="px-4 py-3 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded-xl text-sm font-medium hover:bg-yellow-500/20 transition-all flex items-center justify-center gap-2">
                            <Timer size={16} /> Simular Timeout
                        </button>
                        <button onClick={resetCB} className="px-4 py-3 bg-white/5 text-gray-400 border border-white/10 rounded-xl text-sm font-medium hover:bg-white/10 hover:text-white transition-all flex items-center justify-center gap-2">
                            <ShieldCheck size={16} /> Reset
                        </button>
                    </div>

                    {/* Logs */}
                    <Card>
                        <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
                            <Clock size={16} className="text-neon-blue" /> Log de Transiciones
                        </h2>
                        <div className="space-y-2 max-h-[300px] overflow-y-auto custom-scrollbar">
                            {logs.map((log, i) => (
                                <div key={i} className="flex items-center gap-3 p-3 bg-black/20 rounded-lg text-sm">
                                    <span className="text-gray-600 font-mono text-xs w-16 shrink-0">{log.time}</span>
                                    <span className={`font-mono text-xs px-2 py-0.5 rounded ${STATE_STYLES[log.from].bg} ${STATE_STYLES[log.from].text} border ${STATE_STYLES[log.from].border}`}>{log.from.toUpperCase()}</span>
                                    <ArrowRight size={12} className="text-gray-600 shrink-0" />
                                    <span className={`font-mono text-xs px-2 py-0.5 rounded ${STATE_STYLES[log.to].bg} ${STATE_STYLES[log.to].text} border ${STATE_STYLES[log.to].border}`}>{log.to.toUpperCase()}</span>
                                    <span className="text-gray-500 text-xs truncate">{log.reason}</span>
                                </div>
                            ))}
                        </div>
                    </Card>
                </>
            )}

            {/* Tab: Configuración */}
            {tab === 'config' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-6 flex items-center gap-2">
                        <Settings2 size={16} className="text-neon-blue" /> Parámetros del Circuit Breaker
                    </h2>
                    <div className="grid gap-6">
                        {[
                            { key: 'failureThreshold' as const, label: 'Failure Threshold', desc: 'Número de fallos consecutivos antes de abrir el circuito', unit: 'fallos', min: 1, max: 20 },
                            { key: 'resetTimeout' as const, label: 'Reset Timeout', desc: 'Tiempo en segundos antes de intentar half-open', unit: 'seg', min: 5, max: 300 },
                            { key: 'halfOpenMax' as const, label: 'Half-Open Max Probes', desc: 'Probes exitosos necesarios para cerrar el circuito', unit: 'probes', min: 1, max: 10 },
                            { key: 'monitorWindow' as const, label: 'Monitor Window', desc: 'Ventana de tiempo para contabilizar fallos', unit: 'seg', min: 10, max: 600 },
                        ].map(param => (
                            <div key={param.key} className="flex items-center gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-white font-medium text-sm">{param.label}</span>
                                        <code className="text-[10px] text-gray-600 bg-white/5 px-1.5 py-0.5 rounded font-mono">{param.key}</code>
                                    </div>
                                    <p className="text-gray-500 text-xs mt-0.5">{param.desc}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="range"
                                        min={param.min}
                                        max={param.max}
                                        value={config[param.key]}
                                        onChange={e => setConfig({ ...config, [param.key]: Number(e.target.value) })}
                                        className="w-32 accent-[#00f3ff]"
                                    />
                                    <span className="text-neon-blue font-mono text-sm w-16 text-right">{config[param.key]} <span className="text-gray-600 text-[10px]">{param.unit}</span></span>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {/* Tab: Documentación */}
            {tab === 'docs' && (
                <div className="space-y-6">
                    <Card>
                        <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
                            <Info size={16} className="text-neon-blue" /> ¿Qué es el Circuit Breaker?
                        </h2>
                        <div className="text-gray-400 text-sm space-y-3 leading-relaxed">
                            <p>El <span className="text-white font-medium">Circuit Breaker</span> es un patrón de resiliencia que protege al agente <code className="text-neon-blue bg-neon-blue/5 px-1 rounded">vendor_core.ts</code> contra la saturación de la API de Supabase.</p>
                            <p>Funciona como un disyuntor eléctrico: si detecta demasiados fallos consecutivos, "abre" el circuito y deja de hacer requests, usando datos en caché hasta que la API se recupere.</p>
                        </div>
                    </Card>
                    <Card>
                        <h2 className="text-white font-bold text-sm mb-4">Estados del Circuito</h2>
                        <div className="space-y-4">
                            {Object.entries(STATE_STYLES).map(([key, s]) => (
                                <div key={key} className={`p-4 rounded-lg border ${s.border} ${s.bg}`}>
                                    <div className="flex items-center gap-3">
                                        <div className={s.text}>{s.icon}</div>
                                        <div>
                                            <span className={`font-bold font-mono ${s.text}`}>{s.label}</span>
                                            <p className="text-gray-400 text-sm mt-0.5">{s.desc}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                    <Card>
                        <h2 className="text-white font-bold text-sm mb-4">Implementación en vendor_core.ts</h2>
                        <pre className="bg-black/30 rounded-lg p-4 text-xs text-gray-300 font-mono overflow-auto border border-white/5">{`// Backoff exponencial (ya implementado en Fase 0)
const backoff = Math.min(BASE_INTERVAL * 2 ** consecutiveFailures, MAX_INTERVAL)

// El Circuit Breaker extiende esto:
// - CLOSED: requests normales con backoff
// - OPEN: 0 requests, usa caché local
// - HALF-OPEN: 1 request de prueba cada resetTimeout

// Transiciones:
// CLOSED → OPEN: si failures >= failureThreshold
// OPEN → HALF-OPEN: después de resetTimeout segundos
// HALF-OPEN → CLOSED: si probe exitoso
// HALF-OPEN → OPEN: si probe falla`}</pre>
                    </Card>
                </div>
            )}
        </div>
    )
}
